import React, { useState } from "react";
import { Icon, Button } from "../Component";
import { Spinner } from "reactstrap";
import { api } from "../../utils/api";
import { toast } from "react-toastify";
import { downloadIntelligentSynthesisPdf } from "../../utils/downloadIntelligentSynthesisPdf";

/**
 * @param {() => object} getPayload
 * @param {{ disabled?: boolean, isCompact?: boolean }} options
 */
export function useIntelligentReportGeneration(getPayload, options = {}) {
  const { disabled = false, isCompact = false } = options;
  const [loading, setLoading] = useState(false);
  const [intelligent, setIntelligent] = useState(null);

  const run = async () => {
    if (disabled) return;
    setLoading(true);
    setIntelligent(null);
    try {
      const payload = getPayload();
      const data = await api.postIntelligentReport(payload);
      setIntelligent(data.intelligent || null);
      toast.success(isCompact ? "Synthèse générée" : "Rapport intelligent généré");
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.message ||
        "Impossible de générer le rapport intelligent";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return { loading, intelligent, run };
}

/**
 * Corps affiché après génération : texte + export PDF
 */
export function IntelligentReportSynthesisContent({ intelligent, pdfMeta }) {
  const handleDownloadPdf = () => {
    if (!intelligent) return;
    try {
      downloadIntelligentSynthesisPdf(intelligent, pdfMeta || {});
      toast.success("PDF de la synthèse téléchargé");
    } catch (e) {
      console.error(e);
      toast.error("Impossible de générer le PDF");
    }
  };

  if (!intelligent) return null;

  return (
    <div className="mt-2">
      <h6 className="title mb-2">Synthèse direction</h6>
      <p style={{ whiteSpace: "pre-wrap" }} className="text-dark">
        {intelligent.executive_summary}
      </p>

      {intelligent.key_points?.length > 0 && (
        <>
          <h6 className="title mt-3 mb-2">Points clés</h6>
          <ul className="list list-checked">
            {intelligent.key_points.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </>
      )}

      {intelligent.risks_and_priorities?.length > 0 && (
        <>
          <h6 className="title mt-3 mb-2">Risques & priorités</h6>
          <ul className="list list-checked">
            {intelligent.risks_and_priorities.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </>
      )}

      {intelligent.recommendations?.length > 0 && (
        <>
          <h6 className="title mt-3 mb-2">Recommandations</h6>
          <ul className="list list-checked">
            {intelligent.recommendations.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </>
      )}

      {intelligent.limitations && (
        <p className="text-soft small mt-3 mb-0 border-top pt-3">{intelligent.limitations}</p>
      )}
      {intelligent.model && <p className="text-soft small mb-0">Modèle : {intelligent.model}</p>}

      <div className="d-flex flex-wrap gap-2 mt-3 pt-3 border-top">
        <Button color="secondary" outline size="sm" type="button" onClick={handleDownloadPdf}>
          <Icon name="download" className="me-1" />
          Télécharger la synthèse (PDF)
        </Button>
      </div>
    </div>
  );
}
